import { ApolloClient, InMemoryCache, from } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { createUploadLink } from 'apollo-upload-client';
import { navigate } from 'wouter/use-browser-location';

// Disable apollo error messages in development to prevent Parcel overlay
// see https://www.apollographql.com/docs/react/errors/
// Commented out to prevent console.error calls that trigger Parcel error overlay
// if (process.env.NODE_ENV == 'development') {
//   loadDevMessages();
//   loadErrorMessages();
// }

// Override console.error in development to prevent Parcel error overlay
if (process.env.NODE_ENV === 'development') {
  const originalConsoleError = console.error;
  console.error = (...args) => {
    // Check if this is a GraphQL error that we want to suppress
    const message = args[0];
    if (typeof message === 'string' && message.includes('[GraphQL error]')) {
      // Suppress GraphQL errors in development to prevent Parcel overlay
      return;
    }
    // Allow other errors to be logged normally
    originalConsoleError.apply(console, args);
  };
}

// return the backend URL as configured in the environment variables — unless
// we are on apryl.co, in which case replace the domain accordingly
const determineBackendURL = () => {
  if (document.location.hostname.endsWith('apryl.co')) {
    return process.env.BACKEND_URL.replace('progynyglobal.com', 'apryl.co');
  }
  return process.env.BACKEND_URL;
};

// the terminating link in the apollo link chain - use createUploadLink for file upload support
const httpLink = createUploadLink({
  uri: determineBackendURL(),
  credentials: 'include',
});

// Defining `merge: true` on each root namespace field tells Apollo to shallow
// merge incoming data with existing data instead of replacing, which preserves
// sibling slots and breaks the loop.
const createCache = () =>
  new InMemoryCache({
    addTypename: true,
    typePolicies: {
      Query: {
        fields: {
          members: { merge: true },
          wallets: { merge: true },
          common: { merge: true },
          customers: { merge: true },
          claims: { merge: true },
          claimsInhouse: { merge: true },
          crawford: { merge: true },
          admins: { merge: true },
        },
      },
    },
  });

// Create different link chains for development vs production
const createApolloClient = () => {
  if (process.env.NODE_ENV === 'development') {
    // In development, skip error link entirely to prevent Parcel overlay
    return new ApolloClient({
      link: httpLink,
      cache: createCache(),
      introspection: true,
    });
  } else {
    // In production, use error link for proper error handling
    const errorLink = onError(({ graphQLErrors, networkError }) => {
      if (networkError) {
        console.error('[Network error]', networkError);
        if (networkError.statusCode == 401) {
          navigate('/login');
        }
      }

      if (graphQLErrors) {
        for (const error of graphQLErrors) {
          console.error('[GraphQL error]', error);
          if (error.message == '401-not-authenticated') {
            navigate('/login');
          }
        }
      }
    });

    return new ApolloClient({
      link: from([errorLink, httpLink]),
      cache: createCache(),
      introspection: false,
    });
  }
};

const client = createApolloClient();

export { client };
